import { LogoutOptions } from '@auth0/auth0-spa-js';
import { StatusCodes } from 'http-status-codes';
import { action, computed, observable, runInAction } from 'mobx';
import React from 'react';
import { v4 as uuidv4 } from 'uuid';
import { backendUrl } from '../config/app';
import { MessageAccountBlocked } from '../pages/Dashboard/renderers';
import { storageService } from '../services/storage.service';
import { getUnleashClient } from '../services/unleash-feature-flags.service';
import { MyIDEXXUserType } from '../types/brm-types';
import { GlobalKeys, KeyValueType, ResultObject } from '../types/common-types';
import {
  AgentData,
  AgentsKaseyaInfo,
  AgentStatus,
  CsVersion,
  PracticeConfig,
} from '../types/practice-types';
import {
  EventApi,
  ResourcePermission,
  ResourcesAndPermissions,
  RoleApi,
  SapResourcePermission,
  UserApi,
} from '../types/users-types';
import { VelloEnrollmentAndPracticeAuthUrlResponse } from '../types/vello-types';
import { isDefined } from '../utils/object-utils';
import { getPathWithQuery } from '../utils/url-utils';
import AbstractStore from './AbstractStore';
import {
  AGENTS_PATH,
  CS_VERSIONS_PATH,
  EVENTS_PATH,
  GLOBALS_PATH,
  PRACTICE_PATH,
  ROLES_PATH,
  USERS_PATH,
  VELLO_PATH,
} from './constants';
import RootStore from './RootStore';

const USER_EMAIL = 'USER_EMAIL';

export default class ApiStore extends AbstractStore {
  @observable user: UserApi | undefined;

  @observable userEmail: string;

  @observable private _isAuthenticated = false;

  public auditRequestPromise: Promise<any> = Promise.resolve();

  public logout?: (options?: LogoutOptions) => Promise<void>;

  private getAccessTokenSilently?: (...args: any) => Promise<string>;

  private readonly backEndUrl: string;

  private _userGuid = uuidv4();

  public constructor(rootStore: RootStore) {
    super(rootStore);
    this.userEmail = storageService.getItem(USER_EMAIL, true);
    this.backEndUrl = backendUrl();
  }

  @computed
  public get isAuthenticated(): boolean {
    return this._isAuthenticated;
  }

  @computed
  public get userName(): string {
    return `${this.user?.firstName} ${this.user?.lastName}`;
  }

  @computed
  public get userPermissions(): ResourcePermission[] {
    return ([] as ResourcePermission[]).concat(
      ...(this.user?.globalResourcePermissions || []),
      ...(this.sapId
        ? this.user?.sapResourcePermissions?.find(srp => srp.sapId === this.sapId)
            ?.resourcePermissions || []
        : []),
    );
  }

  @computed
  public get userGlobalPermissions(): ResourcePermission[] {
    return this.user?.globalResourcePermissions || [];
  }

  @computed
  public get userSapPermissions(): SapResourcePermission[] {
    return this.user?.sapResourcePermissions || [];
  }

  @computed
  public get userGlobalRoleLowestLevel(): number {
    return Math.min(...(this.user?.globalRoles?.map(r => r.level) || []));
  }

  get userGuid(): any {
    return this._userGuid;
  }

  set userGuid(userGuid: string) {
    this._userGuid = userGuid;
    const unleashClient = getUnleashClient();
    if (unleashClient) {
      void unleashClient.updateContext({ userId: this.userEmail, sessionId: userGuid });
    }
  }

  @action
  public authenticate(
    getAccessTokenSilently: (...args: any) => Promise<string>,
    logout: (options?: LogoutOptions) => Promise<void>,
    user: MyIDEXXUserType | undefined,
  ): void {
    this.getAccessTokenSilently = getAccessTokenSilently;
    this.logout = logout;
    this._isAuthenticated = true;
    this.userEmail = user?.email || '';
    // setting userId for WalkMe
    (window as any).userId = user?.sub;
    storageService.replace(USER_EMAIL, this.userEmail, true);
  }

  @action
  public async fetchCurrentUserDetails(): Promise<void> {
    this.userGuid = uuidv4();
    try {
      const users = await this.callBackendApiJson<UserApi[]>(USERS_PATH);
      if (users) {
        runInAction(() => (this.user = users[0]));
      }
      if (this.user?.blocked) {
        this.root.globalStore.showOkPopup(
          'Account Is Inactivated',
          React.createElement(MessageAccountBlocked),
        );
      }
    } catch (e: any) {
      console.error(e);
      if (e instanceof TypeError) {
        this.root.globalStore.showOkPopup(
          'Connection error',
          "We're unable to establish a connection. Please check your internet connection and attempt to log in again.",
        );
      } else {
        this.root.globalStore.showOkPopup(
          'Internal server error',
          'An unexpected error occurred.  Please try logging in again. If this issue persists, please contact support.',
        );
      }
    }
  }

  @action.bound
  public async backendPost<T = any>(endpoint: string, body: any): Promise<T> {
    return this.callBackendApiJson<T>(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  }

  @action
  public signOut(): void {
    this.user = undefined;
    this._isAuthenticated = false;
    this.getAccessTokenSilently = undefined;
    if (this.logout) {
      void this.logout({ logoutParams: { returnTo: location.origin } });
    }
  }

  public async callBackendApiJson<T = any>(
    endpoint: string,
    options: RequestInit = {},
  ): Promise<T> {
    const res = await this.callBackendApi(endpoint, options);
    const data = await res.text();
    if (res.status !== StatusCodes.OK.valueOf()) {
      throw new Error(`Http error ${res.status}: ${data}`);
    }
    return JSON.parse(data);
  }

  public async callBackendApiBlob(endpoint: string, options: RequestInit = {}): Promise<Blob> {
    const res = await this.callBackendApi(endpoint, options);
    if (res.status !== StatusCodes.OK.valueOf()) {
      const data = await res.text();
      throw new Error(`Http error ${res.status}: ${data}`);
    }
    return res.blob();
  }

  public userRoleLowestLevel(sapId: string): number {
    const sapRoles = this.user?.sapRoles?.find(r => r.sapId === sapId);
    if (!isDefined(sapRoles)) return this.userGlobalRoleLowestLevel;
    return Math.min(...sapRoles.roles.map(r => r.level), this.userGlobalRoleLowestLevel);
  }

  // *** agents

  public getCachedAgents(sapId: string): Promise<AgentData[]> {
    return this.callBackendApiJson<AgentData[]>(
      getPathWithQuery(AGENTS_PATH, { sapId, refresh: false }),
    );
  }

  public getRefreshedAgents(sapId: string): Promise<AgentData[]> {
    return this.callBackendApiJson<AgentData[]>(
      getPathWithQuery(AGENTS_PATH, { sapId, refresh: true }),
    );
  }

  public getAgentsRefreshStatus(sapId: string, lastAttempt: boolean): Promise<AgentData[]> {
    return this.callBackendApiJson<AgentData[]>(
      getPathWithQuery(AGENTS_PATH, {
        sapId,
        refresh: true,
        allow_update_audit_status: lastAttempt,
      }),
    );
  }

  public getAgent(sapId: string, agentId: string): Promise<AgentData> {
    return this.callBackendApiJson<AgentData>(getPathWithQuery(AGENTS_PATH, { sapId, agentId }));
  }

  public async getAgentUpgradeState(sapId: string, agentId: string): Promise<AgentStatus> {
    const wrapped = await this.callBackendApiJson<ResultObject<AgentStatus>>(
      getPathWithQuery(AGENTS_PATH, { sapId, agentId, check_upgrade_state: true }),
    );
    return wrapped.result;
  }

  public async getAgentsKaseyaInfo(sapId: string, sendEmail?: boolean): Promise<AgentsKaseyaInfo> {
    return this.callBackendApiJson<AgentsKaseyaInfo>(
      getPathWithQuery(AGENTS_PATH, { sapId, get_kaseya_info: true, send_email: sendEmail }),
    );
  }

  public async getAgentsQualificationsPdf(sapId: string): Promise<Blob> {
    return this.callBackendApiBlob(
      getPathWithQuery(AGENTS_PATH, { sapId, qualifications_pdf: true }),
    );
  }

  // *** practice

  public getPractice(sapId: string, initialCheck?: boolean): Promise<PracticeConfig> {
    return this.callBackendApiJson<PracticeConfig>(
      getPathWithQuery(PRACTICE_PATH, { sapId, initial_check: initialCheck }),
    );
  }

  public getAllPractices(): Promise<PracticeConfig[]> {
    return this.callBackendApiJson<PracticeConfig[]>(
      getPathWithQuery(PRACTICE_PATH, { all: true }),
    );
  }

  public updatePracticeAdminVerificationCode(sapId: string): Promise<any> {
    return this.backendPost(PRACTICE_PATH, { action: 'UPDATE_VERIFICATION_CODE', sapId });
  }

  public async submitPracticeAdminVerificationCode(
    sapId: string,
    verificationCode: string,
  ): Promise<boolean> {
    const wrapped = await this.backendPost(PRACTICE_PATH, {
      action: 'SUBMIT_VERIFICATION_CODE',
      sapId,
      verificationCode,
    });
    return wrapped.result;
  }

  public sendInstallationInstructions(sapId: string): Promise<any> {
    return this.backendPost(PRACTICE_PATH, { action: 'SEND_INSTALLATION_INSTRUCTIONS', sapId });
  }

  // *** users

  public findAllUsers(): Promise<UserApi[]> {
    return this.callBackendApiJson<UserApi[]>(getPathWithQuery(USERS_PATH, { all: true }));
  }

  public findUsersForSap(sapId: string): Promise<UserApi[]> {
    return this.callBackendApiJson<UserApi[]>(getPathWithQuery(USERS_PATH, { sapId }));
  }

  public findUsersByLowestRoleLevel(roleLevel?: number): Promise<UserApi[]> {
    return this.callBackendApiJson<UserApi[]>(getPathWithQuery(USERS_PATH, { roleLevel }));
  }

  public async checkSapUsersForResourcePermission(
    sapId: string,
    resource: string,
    permission: string,
  ): Promise<boolean> {
    const wrapped = await this.callBackendApiJson<ResultObject<boolean>>(
      getPathWithQuery(USERS_PATH, { sapId, resource, permission }),
    );
    return wrapped.result;
  }

  // *** roles

  public findAllRoles(): Promise<RoleApi[]> {
    return this.callBackendApiJson<RoleApi[]>(ROLES_PATH);
  }

  public findRoleByGlobalAndLowestLevel(global: boolean, level?: number): Promise<RoleApi[]> {
    return this.callBackendApiJson<RoleApi[]>(getPathWithQuery(ROLES_PATH, { global, level }));
  }

  public findRoleByName(name: string): Promise<RoleApi> {
    return this.callBackendApiJson<RoleApi>(getPathWithQuery(ROLES_PATH, { name }));
  }

  public findRolesByGlobalFlag(global: boolean): Promise<RoleApi[]> {
    return this.callBackendApiJson<RoleApi[]>(getPathWithQuery(ROLES_PATH, { global }));
  }

  public async findAllResourcesAndPermissions(): Promise<ResourcesAndPermissions> {
    return this.callBackendApiJson<ResourcesAndPermissions>(
      getPathWithQuery(ROLES_PATH, { resources_and_permissions: true }),
    );
  }

  // *** events

  public findAllServiceEvents(): Promise<EventApi[]> {
    return this.callBackendApiJson<EventApi[]>(EVENTS_PATH);
  }

  public findScheduledMaintenancesBetweenDates(
    startDateTime: number,
    endDateTime: number,
  ): Promise<EventApi[]> {
    return this.callBackendApiJson<EventApi[]>(
      getPathWithQuery(EVENTS_PATH, { startDateTime, endDateTime }),
    );
  }

  // *** global values

  public async getGlobalValue<T = string | number | boolean>(
    key: (typeof GlobalKeys)[number],
  ): Promise<T | null> {
    const pair = await this.callBackendApiJson<KeyValueType<T>>(
      getPathWithQuery(GLOBALS_PATH, { key }),
    );
    return pair.value;
  }

  // *** Vello

  public getVelloEnrollmentAndPracticeAuthUrl(
    sapId: string,
  ): Promise<VelloEnrollmentAndPracticeAuthUrlResponse> {
    return this.backendPost<VelloEnrollmentAndPracticeAuthUrlResponse>(VELLO_PATH, {
      action: 'GET_PRACTICE_AUTH_URL',
      sapId,
    });
  }

  // *** CsVersions

  public getCsVersions(): Promise<CsVersion[]> {
    return this.callBackendApiJson<CsVersion[]>(getPathWithQuery(CS_VERSIONS_PATH));
  }

  private async callBackendApi(endpoint: string, options: RequestInit = {}): Promise<Response> {
    const { headers, ...others } = options;
    if (!this.getAccessTokenSilently) {
      throw new Error(`Auth0 getAccessTokenSilently() method is not set`);
    }
    const accessToken = await this.getAccessTokenSilently();
    return fetch(`${this.backEndUrl}${endpoint}`, {
      ...others,
      headers: { ...headers, Authorization: `Bearer ${accessToken}`, 'x-user-guid': this.userGuid },
    });
  }
}
